import { useCallback, useContext, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useLazyQuery, useMutation } from '@apollo/client'
import { QuizContext, TQuiz } from '@health-zelas/quiz'

import {
  HealthSimulatorProduct,
  LeadApps,
  ListSimulatorProductsDocument,
  SaveLeadDocument
} from '@packages/services/src/apolloClient/gql/graphql'

import { decodeBase64, encodeBase64 } from '../../utils/base64'
import { formatPrice } from '../../utils/formatPrice'
import Cookies from 'js-cookie'

const removeQuizSensitiveData = (quiz: TQuiz) => {
  const newQuiz = {
    ...quiz,
    email: '',
    firstname: '',
    phone: ''
  }

  return newQuiz
}

function getAgeRanges(objeto: Record<string, string>) {
  const ageRange = []

  for (const chave in objeto) {
    if(!!objeto[chave]){
      if (objeto.hasOwnProperty(chave)) {
        const faixa = chave.match(/(\d+)_*(\d*)_(\d*)\s*anos/)

        if (faixa) {
          const min = parseInt(faixa[1])
          const max = faixa[2] ? parseInt(faixa[2]) : parseInt(faixa[3])
          ageRange.push({ min, max })
        }
      }
    }
  }

  return ageRange
}

type AgeRange = {
  min: number
  max: number
}

export function useResultController(){
  const [shareResultModalIsVisible, setShareResultModalIsVisible] = useState(false)
  const [products, setProducts] = useState<HealthSimulatorProduct[]>()
  const [alternativeProducts, setAlternativeProducts] = useState<HealthSimulatorProduct[]>()
  const [isLoading, setIsLoading] = useState(true)

  const { quiz } = useContext(QuizContext)
  const [saveLead] = useMutation(SaveLeadDocument)

  const [searchParams] = useSearchParams()
  const shareParam = searchParams.get('share') ?? ''

  const quizWithoutPersonalData = removeQuizSensitiveData(quiz)
  const base64Quiz = encodeBase64(JSON.stringify(quizWithoutPersonalData))
  const shareableLink = `https://${window.location.host}/simulador/resultado?share=${base64Quiz}`

  const [searchProducts] = useLazyQuery(ListSimulatorProductsDocument)

  const loadProducts = useCallback(async () => {
    try {
      const decodedParam = JSON.parse(shareParam ? decodeBase64(shareParam) : '{}')

      let apartment_accommodation: boolean
      let co_participation: boolean
      let national_coverage: boolean
      let budget: number
      let ageRange: AgeRange[]

      if(!!shareParam && !!decodedParam){
        apartment_accommodation = decodedParam?.acomodacao___quiz === 'Apartamento'
        co_participation = decodedParam?.coparticipacao__clonado_ === 'true'
        national_coverage = decodedParam?.abrangencia___quiz === "Em todo o Brasil"
        budget = Number(decodedParam.budget?.replace(/\D/g, ''))
        const {
          n__e_idade_0_18_anos,
          n__e_idade_19_23_anos,
          n__e_idade_24_28_anos,
          n__e_idade_29_33_anos,
          n__e_idade_34_38_anos,
          n__e_idade_39_43_anos,
          n__e_idade_44_48_anos,
          n__e_idade_49_53_anos,
          n__e_idade_54_58_anos,
          n__e_idade_59_100_anos
        } = decodedParam
        ageRange = getAgeRanges({
          n__e_idade_0_18_anos,
          n__e_idade_19_23_anos,
          n__e_idade_24_28_anos,
          n__e_idade_29_33_anos,
          n__e_idade_34_38_anos,
          n__e_idade_39_43_anos,
          n__e_idade_44_48_anos,
          n__e_idade_49_53_anos,
          n__e_idade_54_58_anos,
          n__e_idade_59_100_anos
        })
      } else {
        const {
          acomodacao___quiz,
          budget: budgetQuiz,
          coparticipacao__clonado_,
          abrangencia___quiz,
          n__e_idade_0_18_anos,
          n__e_idade_19_23_anos,
          n__e_idade_24_28_anos,
          n__e_idade_29_33_anos,
          n__e_idade_34_38_anos,
          n__e_idade_39_43_anos,
          n__e_idade_44_48_anos,
          n__e_idade_49_53_anos,
          n__e_idade_54_58_anos,
          n__e_idade_59_100_anos
        } = quiz
        apartment_accommodation = acomodacao___quiz === 'Apartamento'
        co_participation = coparticipacao__clonado_ === 'true'
        national_coverage = abrangencia___quiz === "Em todo o Brasil"
        budget = Number(budgetQuiz.replace(/\D/g, ''))
        ageRange = getAgeRanges({
          n__e_idade_0_18_anos,
          n__e_idade_19_23_anos,
          n__e_idade_24_28_anos,
          n__e_idade_29_33_anos,
          n__e_idade_34_38_anos,
          n__e_idade_39_43_anos,
          n__e_idade_44_48_anos,
          n__e_idade_49_53_anos,
          n__e_idade_54_58_anos,
          n__e_idade_59_100_anos
        })
      }

      const { data } = await searchProducts({
        variables: {
          filterFields: {
            ageRange,
            apartment_accommodation,
            budget,
            co_participation,
            national_coverage,
          }
        }
      })

      const newProducts = data?.listSimulatorProducts?.products as HealthSimulatorProduct[]
      const newAlternativeProducts = data?.listSimulatorProducts?.alternativeProducts as HealthSimulatorProduct[]
      setAlternativeProducts(newAlternativeProducts)
      return setProducts(newProducts)
    } catch {
      setProducts([])
    } finally {
      setIsLoading(false)
    }
  }, [quiz, searchProducts, shareParam])

  const sendLeadPlatformData = useCallback(async () => {
    const leadDataFromSimulatorState = JSON.parse(localStorage.getItem('zelasSimulator') || '{}')
    const leadId = Cookies.get('escaleLead') || localStorage.getItem('escaleLead') || ''

    const plansOfferedInString = products?.reduce((acc, product) => {
      return `${acc}${product.name},`
    }, '').slice(0, -1)

    const planValuesOfferedInString = products?.reduce((acc, product) => {
      const productPrice = formatPrice(product.price || 0)

      return `${acc}${productPrice},`
    }, '').slice(0, -1)

    const { data } = await saveLead({
      variables: {
        leadData: {
          ...leadDataFromSimulatorState,
          plano_ofertado: plansOfferedInString,
          possui_cnpj: leadDataFromSimulatorState.possui_cnpj === 'Sim' ? true : false,
          valor_do_plano_ofertado: planValuesOfferedInString,
          website: 'saude.zelas.com.br/simulador',
          fonte_zd: 'organic_form_simulador',
          leadId,
          url_resultado_simulador: shareableLink
        },
        from: LeadApps.Simulator
      }
    })

    const leadIdFromBff = data?.saveLead?.leadId || ''
    localStorage.setItem('escaleLead', leadIdFromBff)

    return data
  }, [products, saveLead, shareableLink])

  function handleOpenShareResultModal(){
    setShareResultModalIsVisible(true)
  }

  function handleCloseShareResultModal(){
    setShareResultModalIsVisible(false)
  }

  useEffect(() => {
    loadProducts()

    if(!isLoading){
      sendLeadPlatformData()
    }
  }, [isLoading, loadProducts, sendLeadPlatformData])

  return {
    handleOpenShareResultModal,
    handleCloseShareResultModal,
    shareResultModalIsVisible,
    products,
    alternativeProducts,
    isLoading,
    shareableLink
  }
}
